import { useId } from 'react';
import type { ReactNode } from 'react';

/**
 * One dashboard box that folds away.
 *
 * Built on `.section-toggle` and `.section-toggle-caret`, which already exist
 * and are already what a foldable heading looks like in this app — the Lists
 * view uses them. A `.tracker-dashboard-toggle` would have been a second
 * object doing the first one's job, which is the duplicate the style guard
 * caught last time and the reason it exists.
 *
 * The title stays visible when folded. A collapsed box with no name is a
 * mystery box, and the point of folding these is to skim past them, which
 * needs the label more than the open state does.
 */
export function DashboardSection({
  title,
  collapsed,
  onToggle,
  children,
}: {
  title: string;
  collapsed: boolean;
  onToggle: () => void;
  children: ReactNode;
}) {
  const bodyId = useId();
  return (
    <div className="tracker-dashboard-card">
      <button
        type="button"
        className="section-toggle tracker-dashboard-card-toggle"
        aria-expanded={!collapsed}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <span aria-hidden="true" className="section-toggle-caret">
          {collapsed ? '▸' : '▾'}
        </span>
        <span className="tracker-dashboard-card-title">{title}</span>
      </button>
      {/* Unmounted rather than hidden. These boxes render tables and bar rows
          over every lead; keeping seven of them mounted behind display:none
          would mean the fold saves the eye and costs the browser exactly as
          much as before. */}
      {collapsed ? null : <div id={bodyId}>{children}</div>}
    </div>
  );
}
